import { useRef } from 'react'
import { useTranslation } from 'next-i18next'

import { useAppDispatch } from '~/redux/hooks'
import ArrowButton from '~/checklist/components/ArrowButton'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { EventDetectionMethod, EventType } from '~/behavior-tracking/types'
import { logUserActivity } from '~/behavior-tracking/utility/view-event-logs.utility'

import { SubjectListItem, Level } from '../types/mapping-quiz.types'
import { setScrollItemIdOnTopicPage } from '../slice/persistCurriculum.slice'
import { useGetCurriculumLastActivityQuery } from '../apis/get-curriculum-user-stats.api'

import PrerequisiteLevel from './PrerequisiteLevel'

interface Props {
  key: string
  userId: number | null
  subjectId: string
  scrollItemId: string
  moduleIndex: number
  viewKey: string
  curriculumSubject: SubjectListItem
  isSubjectExpanded: boolean
  disableScroll?: boolean
  grade?: string
  topic?: string
  subTopic?: string
  onClick: (subjectId: string, isExpanded: boolean) => void
}
const PrerequisitesBootcampModule = ({
  key,
  userId,
  subjectId,
  scrollItemId,
  moduleIndex,
  viewKey,
  curriculumSubject,
  isSubjectExpanded,
  disableScroll = false,
  grade,
  topic,
  subTopic,
  onClick,
}: Props): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation('curriculum')
  const moduleRef = useRef<HTMLDivElement>(null)

  const lastActivity = useGetCurriculumLastActivityQuery(
    { eventName: EventType.CURRICULUM_EVENT },
    {
      skip: !userId,
    },
  ).data

  const onLevelClick = async (levelTitle: string, id: string): Promise<void> => {
    if (userId) {
      const eventData = {
        detectBy: EventDetectionMethod.ON_SELECT_PREREQUISITE_LEVEL,
        title: String(levelTitle || ''),
        curriculumLevelId: String(id || ''),
        quarterNo: -1,
        gradeLevel: -1,
        grade: String(grade || ''),
        topic: String(topic || ''),
        subTopic: String(subTopic || ''),
        levelTitle: String(levelTitle || ''),
      }

      const event = {
        eventName: EventType.CURRICULUM_EVENT,
        viewKey,
        extra: eventData,
      }
      dispatch(setScrollItemIdOnTopicPage(id))
      await logUserActivity(userId, event)
    }
  }

  return (
    <div key={key}>
      <div className="relative mt-8 px-3">
        <div
          className={`border-[3px] ${
            curriculumSubject.isCompleted
              ? 'bg-purple-darker border-white shadow-task-white'
              : 'bg-white border-green-strong shadow-task-vivid-green'
          } rounded p-4 cursor-pointer`}
          onClick={() => onClick(subjectId, isSubjectExpanded)}
        >
          <div className="absolute border-2 border-white -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-neon-green rounded-full w-8 h-8 flex items-center justify-center shadow-md">
            <span className="text-white font-bold">{moduleIndex}</span>
          </div>

          <div className="relative space-x-3 flex items-start justify-between h-full w-full">
            <div className="w-full  absolute -left-2 -top-1.5">
              {curriculumSubject.isCompleted ? (
                <img
                  className="absolute z-5 -top-10 -left-6"
                  height={50}
                  src="https://assets.houseofmath.com/icons/emojis/clapping-hands-animated.png"
                  width={50}
                />
              ) : (
                <ResponsiveImage
                  alt="Puzzle Icon"
                  height={32}
                  imageClassName="w-8 h-8"
                  imageWidths="32"
                  srcKey="onboarding/icons/puzzle_piece.svg"
                  width={32}
                />
              )}
            </div>
            <div className="flex flex-col min-w-62 text-center" ref={moduleRef}>
              <p className={`text-xs ${curriculumSubject.isCompleted ? 'text-white' : 'text-gray-darker'}`}>
                {t('mappingQuiz.topicsIn')}
              </p>
              <p className={`text-md ${curriculumSubject.isCompleted ? 'text-white' : 'text-purple'}  break-words`}>
                {curriculumSubject.title}
              </p>
            </div>
            <button className="absolute top-0 right-0">
              <ArrowButton
                color={`${curriculumSubject.isCompleted ? 'text-white' : 'text-green-avocado'}`}
                expanded={isSubjectExpanded}
              />
            </button>
          </div>
        </div>
      </div>

      <div
        className={` transition-all delay-150 duration-500 overflow-hidden px-4 ${
          isSubjectExpanded ? 'max-h-800' : 'max-h-0' // max-h is needed for a smooth transition, the data is dynamic so I set it to a high value
        }`}
      >
        {curriculumSubject.levels.map((level: Level) => {
          const levelId = level.levelIds[0]
          const isLastVisited = lastActivity?.curriculumLevelId === levelId
          return (
            <PrerequisiteLevel
              disableScroll={disableScroll}
              iconType={level.iconType}
              isCompleted={level.isCompleted}
              isLastVisited={isLastVisited}
              key={levelId}
              levelId={levelId}
              onLevelClick={onLevelClick}
              path={level.path}
              scrollItemId={scrollItemId}
              title={level.title}
              type={level.type}
            />
          )
        })}
      </div>
    </div>
  )
}

export default PrerequisitesBootcampModule
